import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { webAssets } from '../../../utils/webAssets'
import AppStoreBadge from '@/components/svgs/AppstoreBadge'
import GooglePlayBadge from '@/components/svgs/GoogleplayBadge'
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@/utils/appStoreLinks'
import {
  AGENZIA_ENTRATE_AUTHORIZATION_PDF,
  CAF_AUTHORIZATION_PDF,
  PATRONATO_AUTHORIZATION_PDF,
  getFooterCopy,
  getFooterDescriptionParts,
} from './footerCopy'
import {
  normalizeLayoutPath,
  shouldDisableLayoutElementClick,
  shouldHideLayoutElement,
} from './layoutElementVisibility'
import { preloadAuthorizationPdfs } from './authorizationPdfCache'

const AuthorizationPdfModal = dynamic(() => import('./AuthorizationPdfModal'), {
  ssr: false,
})

const footerAuthorityLinkClassName =
  'underline underline-offset-2 transition-colors hover:text-green'

const FooterDescription = ({ className, description }) => (
  <p className={className}>
    {getFooterDescriptionParts(description).map((part, index) => {
      const isLinkedAuthority = typeof part !== 'string'

      if (!isLinkedAuthority) {
        return <span key={`footer-description-${index}`}>{part}</span>
      }

      const opensInNewTab = Boolean(part.opensInNewTab)

      return (
        <a
          key={`footer-description-link-${index}`}
          href={part.href}
          target={opensInNewTab ? '_blank' : undefined}
          rel={opensInNewTab ? 'noopener noreferrer' : undefined}
          className={footerAuthorityLinkClassName}
        >
          {part.label}
        </a>
      )
    })}
  </p>
)

const FooterUpper = () => {
  const { t } = useTranslation('HomeLayout')
  const { locale, asPath } = useRouter()
  const copy = getFooterCopy(locale)
  const isLandingPage = normalizeLayoutPath(asPath).startsWith('/lp')
  const tf = (key, defaultValue, options = {}) =>
    t(key, { defaultValue, ...options })
  const hideFooterUpperLogo = shouldHideLayoutElement('footerUpperLogo', asPath)
  const hideFooterDescription = shouldHideLayoutElement('footerDescription', asPath)
  const hideFooterAppBadges = shouldHideLayoutElement('footerAppBadges', asPath)
  const hideFooterQuickLinks = shouldHideLayoutElement('footerQuickLinks', asPath)
  const hideFooterInfoLinks = shouldHideLayoutElement('footerInfoLinks', asPath)
  const hideFooterAddress = shouldHideLayoutElement('footerAddress', asPath)
  const disableFooterUpperLogoClick = shouldDisableLayoutElementClick(
    'footerUpperLogo',
    asPath
  )
  const [selectedAuthorization, setSelectedAuthorization] = useState(null)

  useEffect(() => {
    const canScheduleIdleWork = typeof window.requestIdleCallback === 'function'
    const preloadPdfs = () => {
      preloadAuthorizationPdfs([
        CAF_AUTHORIZATION_PDF,
        PATRONATO_AUTHORIZATION_PDF,
        AGENZIA_ENTRATE_AUTHORIZATION_PDF,
      ])
    }

    if (canScheduleIdleWork) {
      const idleCallbackId = window.requestIdleCallback(preloadPdfs)

      return () => window.cancelIdleCallback(idleCallbackId)
    }

    const timeoutId = window.setTimeout(preloadPdfs, 1)

    return () => window.clearTimeout(timeoutId)
  }, [])

  const quickLinks = [
    { href: '/about-us', label: tf('footer.AboutUS', copy.aboutUs) },
    { href: '/blog', label: tf('footer.blog', copy.blog) },
    // { href: '/faqs', label: tf('footer.info.FAQ', copy.faq) },
  ]

  const infoLinks = [
    { href: '/terms-and-conditions', label: tf('footer.terms', copy.terms) },
    {
      href: '/privacy-and-policy',
      label: tf('footer.info.PrivacyPolicy', copy.privacyPolicy),
    },
    {
      href: '/cookie-policy',
      label: tf('footer.info.CookiePolicy', copy.cookiePolicy),
    },
  ]

  const authorizationLinks = [
    {
      href: CAF_AUTHORIZATION_PDF,
      label: tf('footer.authorizations.caf', copy.caf),
    },
    {
      href: PATRONATO_AUTHORIZATION_PDF,
      label: tf('footer.authorizations.patronato', copy.patronato),
    },
    {
      href: AGENZIA_ENTRATE_AUTHORIZATION_PDF,
      label: tf('footer.authorizations.agenziaEntrate', copy.agenziaEntrate),
    },
  ]

  return (
    <footer
      id='site-footer'
      className='bg-[linear-gradient(180deg,#262f39_0%,#0c1015_100%)] px-16 pt-40 text-white sm:px-24 lg:px-0 lg:pt-[88px]'
    >
      <div className='container mx-auto'>
        {hideFooterUpperLogo ? null : (
          disableFooterUpperLogoClick ? (
            <div className='mb-24 hidden lg:inline-block'>
              <Image
                alt='Logo SmartCAF'
                src={webAssets.logoDark}
                className='h-[67px] w-[96px]'
                width={96}
                height={67}
              />
            </div>
          ) : (
            <Link aria-label='Vai alla home page SmartCAF' href='/' className='mb-24 hidden lg:inline-block'>
              <Image
                alt='Logo SmartCAF'
                src={webAssets.logoDark}
                className='h-[67px] w-[96px]'
                width={96}
                height={67}
              />
            </Link>
          )
        )}

        <div className='grid gap-x-24 gap-y-32 pb-[32px] sm:pb-[48px] md:grid-cols-2 lg:gap-x-32 lg:gap-y-40 lg:pb-[64px] xl:grid-cols-[minmax(16rem,1.4fr)_repeat(4,minmax(8rem,1fr))] xl:gap-x-28'>
          <div className='max-w-[340px] xl:max-w-none'>
            {hideFooterUpperLogo && hideFooterDescription ? null : (
              <div className='mb-24 flex items-start gap-16 lg:hidden'>
                {hideFooterUpperLogo ? null : (
                  disableFooterUpperLogoClick ? (
                    <div className='shrink-0'>
                      <Image
                        alt='Logo SmartCAF'
                        src={webAssets.logoDark}
                        className='h-[60px] w-[86px]'
                        width={86}
                        height={60}
                      />
                    </div>
                  ) : (
                    <Link aria-label='Vai alla home page SmartCAF' href='/' className='shrink-0'>
                      <Image
                        alt='Logo SmartCAF'
                        src={webAssets.logoDark}
                        className='h-[60px] w-[86px]'
                        width={86}
                        height={60}
                      />
                    </Link>
                  )
                )}
                {hideFooterDescription ? null : (
                  <FooterDescription
                    className='mb-0 flex-1 text-[14px] font-bold leading-[1.5] text-white'
                    description={tf('footer.description', copy.description)}
                  />
                )}
              </div>
            )}

            {hideFooterDescription ? null : (
              <FooterDescription
                className='mb-24 hidden text-[16px] font-bold leading-[1.6] text-white lg:block'
                description={tf('footer.description', copy.description)}
              />
            )}

            {hideFooterAppBadges ? null : (
              <div className='flex items-center gap-12'>
                <a
                  href={APP_STORE_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='Download SmartCAF on the App Store'
                  className='flex-1'
                >
                  <div className='[&>svg]:h-48 [&>svg]:w-full'>
                    <AppStoreBadge />
                  </div>
                </a>
                <a
                  href={GOOGLE_PLAY_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='Download SmartCAF on Google Play'
                  className='flex-1'
                >
                  <div className='[&>svg]:h-48 [&>svg]:w-full'>
                    <GooglePlayBadge />
                  </div>
                </a>
              </div>
            )}
          </div>

          <div>
            <h3 className='mb-16 text-[16px] font-bold leading-[20px] text-white lg:mb-24 lg:text-[18px] lg:leading-[22px]'>
              {tf('footer.authorizations.title', copy.authorizationsTitle)}
            </h3>
            <ul className='flex flex-col gap-y-16 text-[16px] font-medium leading-[24px] text-green lg:font-normal lg:text-white'>
              {authorizationLinks.map((link) => (
                <li key={link.href}>
                  <button
                    type='button'
                    className='inline-flex items-baseline gap-8 border-0 bg-transparent p-0 text-inherit transition-colors hover:text-green'
                    onClick={() => setSelectedAuthorization(link)}
                  >
                    <span>{link.label}</span>
                    <span className='text-[11px] font-medium uppercase tracking-[0.04em] text-white/70'>
                      PDF
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {hideFooterQuickLinks ? null : (
            <div>
              <h3 className='mb-16 text-[16px] font-bold leading-[20px] text-white lg:mb-24 lg:text-[18px] lg:leading-[22px]'>
                {tf('footer.quickLinks', copy.quickLinks)}
              </h3>
              <ul className='flex flex-col gap-y-16 text-[16px] font-medium leading-[20px] text-green lg:font-normal lg:text-white'>
                {quickLinks.map((link) => (
                  <li key={link.href}>
                    <Link className='transition-colors hover:text-green' href={link.href}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hideFooterInfoLinks ? null : (
            <div>
              <h4 className='mb-16 text-[16px] font-bold leading-[20px] text-white lg:mb-24 lg:text-[18px] lg:leading-[22px]'>
                {tf('footer.info.title', copy.infoTitle)}
              </h4>
              <ul className='flex flex-col gap-y-16 text-[16px] font-medium leading-[20px] text-green lg:font-normal lg:text-white'>
                {infoLinks.map((link) => (
                  <li key={link.href}>
                    <Link className='transition-colors hover:text-green' href={link.href}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div className='space-y-12 lg:space-y-16 text-[16px] leading-[20px] text-white'>
              {hideFooterAddress ? null : (
                <div className='flex items-center gap-8 text-white'>
                  <Image
                    alt='Icona posizione'
                    src={webAssets.location}
                    width={14}
                    height={20}
                    className='h-20 w-14 lg:h-20 lg:w-14'
                  />
                  <p className='mb-0 text-[16px] font-medium leading-[20px]'>{tf('footer.address', copy.address)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {selectedAuthorization ? (
        <AuthorizationPdfModal
          open
          title={selectedAuthorization.label}
          file={selectedAuthorization.href}
          onClose={() => setSelectedAuthorization(null)}
        />
      ) : null}
    </footer>
  )
}

export default FooterUpper
